import React, { useEffect, useState } from 'react';
import { 
  Card, 
  CardHeader, 
  CardBody, 
  Button, 
  Row, 
  Col, 
  Spinner, 
  Table, 
  Badge,
  Alert,
  UncontrolledTooltip,
  Progress,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input
} from 'reactstrap';
import { 
  getInvoiceById, 
  sendInvoiceEmail, 
  downloadInvoicePdf,
  markInvoiceAsPaid,
  getInvoicePdfPreviewHtml
} from '../../services/api/invoice-api';
import projectsService from "../../services/api/projects-service";


const InvoiceDetail = ({ projectId, invoiceId, onBack }) => {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [error, setError] = useState(null);
  const [actionMessage, setActionMessage] = useState('');
  const [paymentModal, setPaymentModal] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState({
    amount: 0,
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'bank_transfer',
    reference_number: '',
    notes: ''
  });
  const [calculatedTotals, setCalculatedTotals] = useState({
    subtotal: 0,
    tax: 0,
    lesstax: 0,
    total: 0,
    amountToPaid: 0,
  });
  const [projectName, setProjectName] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');

  useEffect(() => {
    fetchInvoiceDetails();
  }, [invoiceId]);

  useEffect(() => {
  if (invoice?.totals) {
    const subtotal = Number(invoice.totals?.subtotal.toFixed(2));
    const tax = Number((invoice.totals?.tax_amount || subtotal * 0.18).toFixed(2));
    const lesstax = Number(invoice.totals?.tds.toFixed(2)) || Number((subtotal * 0.02).toFixed(2));
    console.log("Less tax:- ", lesstax);
    const total = Number((subtotal + tax).toFixed(2));
    const amountToPaid = Number(invoice.totals?.total_amount.toFixed(2)) || Number((total - lesstax).toFixed(2));


    setCalculatedTotals({
      subtotal,
      tax,
      lesstax,
      total,
      amountToPaid
    });
  }
}, [invoice?.totals]);

  useEffect(() => {
    const fetchProjectDetails = async () => {
      try {
        const res = await projectsService.getProjectDetails(projectId);
        if (res?.status && res?.data?.name) {
          setProjectName(res.data.name);
        }
      } catch (err) {
        console.error('Failed to fetch project details:', err);
      }
    };

    if (projectId) {
      fetchProjectDetails();
    }
  }, [projectId]);

  const toggleModal = () => setModalOpen(!modalOpen);

  const fetchPreview = async () => {
    try {
      const response = await getInvoicePdfPreviewHtml(invoiceId);
      setPreviewHtml(response.data.html); // this is HTML string
      setModalOpen(true);
    } catch (err) {
      console.error("Failed to load invoice preview:", err);
    }
  };

  const fetchInvoiceDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getInvoiceById(invoiceId);
      setInvoice(res.data.invoice);
      // Set default payment amount to remaining balance
      const remainingAmount = (res.data.invoice.totals?.total_amount || 0) - (res.data.invoice.totals?.paid_amount || 0);
      setPaymentDetails(prev => ({ ...prev, amount: Number(remainingAmount.toFixed(2)) }));
    } catch (err) {
      setError('Failed to load invoice details. Please try again.');
      console.error('Error fetching invoice:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async () => {
    try {
      setSending(true);
      await sendInvoiceEmail(invoice._id);
      setActionMessage('Email sent successfully!');
      setTimeout(() => setActionMessage(''), 3000);
    } catch (err) {
      setActionMessage('Failed to send email. Please try again.');
      setTimeout(() => setActionMessage(''), 3000);
    } finally {
      setSending(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setDownloading(true);
      const res = await downloadInvoicePdf(invoice._id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice-${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setActionMessage('PDF downloaded successfully!');
      setTimeout(() => setActionMessage(''), 3000);
    } catch (err) {
      setActionMessage('Failed to download PDF. Please try again.');
      setTimeout(() => setActionMessage(''), 3000);
    } finally {
      setDownloading(false);
    }
  };

  const handleMarkAsPaid = async () => {
    try {
      setMarkingPaid(true);
      console.log('Mark As Paid:- ', paymentDetails)
      await markInvoiceAsPaid(invoice._id, paymentDetails);
      setActionMessage('Invoice marked as paid successfully!');
      setPaymentModal(false);
      setTimeout(() => setActionMessage(''), 3000);
      // Refresh invoice details
      await fetchInvoiceDetails();
    } catch (err) {
      setActionMessage('Failed to mark invoice as paid. Please try again.');
      setTimeout(() => setActionMessage(''), 3000);
    } finally {
      setMarkingPaid(false);
    }
  };

  const togglePaymentModal = () => {
    setPaymentModal(!paymentModal);
  };

  const handlePaymentDetailChange = (field, value) => {
    setPaymentDetails(prev => ({
      ...prev,
      [field]: field === 'amount' ? (isNaN(value) ? 0 : value) : value
    }));
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'paid': return 'success';
      case 'sent': return 'info';
      case 'pending': return 'warning';
      case 'draft': return 'primary';
      case 'overdue': return 'danger';
      default: return 'secondary';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'INR'
    }).format(amount || 0);
  };

  const getPaymentProgress = () => {
    if (!invoice.totals) return 0;
    const paid = invoice.totals.paid_amount || 0;
    const total = invoice.totals.total_amount || 0;
    return paid > 0 ? (paid / total) * 100 : 0;
  };

  const getRemainingAmount = () => {
    if (!invoice.totals) return 0;
    return (calculatedTotals.amountToPaid || 0) - (invoice.totals.paid_amount || 0);
  };

  const canMarkAsPaid = () => {
    return invoice.status !== 'paid' && getRemainingAmount() > 0;
  };

  if (loading) {
    return (
      <div className="content">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <Spinner color="primary" size="lg" />
            <p className="mt-3 text-muted">Loading invoice details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="content">
        <Row className="justify-content-center">
          <Col lg="8">
            <Alert color="danger" className="text-center">
              <i className="nc-icon nc-simple-remove me-2" />
              {error}
              <div className="mt-3">
                <Button color="primary" onClick={fetchInvoiceDetails} className="me-2">
                  <i className="nc-icon nc-refresh-69 me-1" />
                  Retry
                </Button>
                <Button color="secondary" onClick={onBack}>
                  <i className="nc-icon nc-minimal-left me-1" />
                  Back to List
                </Button>
              </div>
            </Alert>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className="content">
      {/* Action Message Alert */}
      {actionMessage && (
        <Row className="mb-3">
          <Col>
            <Alert 
              color={actionMessage.includes('success') ? 'success' : 'danger'} 
              className="text-center"
            >
              <i className={`nc-icon ${actionMessage.includes('success') ? 'nc-check-2' : 'nc-simple-remove'} me-2`} />
              {actionMessage}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Header Card */}
      <Row className="mb-4">
        <Col>
          <Card className="shadow-sm">
            <CardHeader className="bg-white border-0">
              <Row className="align-items-center">
                <Col>
                  <div className="d-flex align-items-center">
                    <Button 
                      color="secondary" 
                      size="sm" 
                      onClick={onBack}
                      className="btn-round me-3"
                    >
                      <i className="nc-icon nc-minimal-left" />
                    </Button>
                    <div>
                      <h4 className="mb-1">Invoice #{invoice.invoice_number}</h4>
                      <small className="text-muted">
                        Created on {new Date(invoice.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </small>
                    </div>
                  </div>
                </Col>
                <Col xs="auto">
                  <div className="d-flex align-items-center">
                    {/* Mark as Paid Button
                    {canMarkAsPaid() && (
                      <>
                        <Button 
                          color="warning" 
                          className="me-2 py-2 btn-round" 
                          onClick={togglePaymentModal}
                          id="mark-paid-btn"
                          size="sm"
                        >
                          <i className="nc-icon nc-check-2 me-1" />
                          Mark as Paid
                        </Button>
                        <UncontrolledTooltip placement="top" target="mark-paid-btn">
                          Mark this invoice as paid
                        </UncontrolledTooltip>
                      </>
                    )} */}
                    
                    {/* Download PDF Button */}
                    <Button 
                      color="success" 
                      className="me-2 py-2 btn-round" 
                      onClick={handleDownloadPdf} 
                      disabled={downloading}
                      id="download-btn"
                      size="sm"
                    >
                      <i className="nc-icon nc-cloud-download-93 me-1" />
                      {downloading ? 'Downloading...' : 'PDF'}
                    </Button>
                    <UncontrolledTooltip placement="top" target="download-btn">
                      Download invoice as PDF
                    </UncontrolledTooltip>
                    
                    {/* Send Email Button */}
                    {invoice.status !== 'paid' && (
                      <>
                        <Button
                          color="info"
                          onClick={handleSendEmail}
                          disabled={sending}
                          className="btn-round py-2"
                          id="email-btn"
                          size="sm"
                        >
                          <i className="nc-icon nc-email-85 me-1" />
                          {sending ? 'Sending...' : 'Email'}
                        </Button>
                        <UncontrolledTooltip placement="top" target="email-btn">
                          Send invoice via email
                        </UncontrolledTooltip>
                      </>
                    )}

                     <Button 
                      color="primary" 
                      onClick={fetchPreview} 
                      className="btn-round py-2"
                      id="preview-btn"
                      size="sm"
                      >
                        <i className="fa fa-eye me-1" />
                        Preview Invoice
                      </Button>
                    <UncontrolledTooltip placement="top" target="preview-btn">
                      Get Invoice preview
                    </UncontrolledTooltip>
                  </div>
                </Col>
              </Row>
            </CardHeader>
          </Card>
        </Col>
      </Row>

      {/* Invoice Summary */}
      <Row className="mb-4">
        <Col lg="8">
          <Card className="shadow-sm h-100">
            <CardHeader className="bg-white border-0">
              <h5 className="mb-0">
                <i className="nc-icon nc-circle-10 me-2 text-primary" />
                Invoice Details
              </h5>
            </CardHeader>
            <CardBody>
              <Row>
                <Col md="6">
                  <div className="info-section">
                    <h6 className="text-muted mb-3">INVOICE INFORMATION</h6>
                    <div className="info-item mb-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="font-weight-bold">Status:</span>
                        <Badge 
                          color={getStatusBadgeColor(invoice.status)}
                          className="text-capitalize px-3 py-2"
                        >
                          {invoice.status}
                        </Badge>
                      </div>
                    </div>
                    <div className="info-item mb-3">
                      <div className="d-flex justify-content-between">
                        <span className="font-weight-bold">Project:</span>
                        <span className="text-right">{projectName}</span>
                      </div>
                    </div>
                    {invoice.publisher_id?.email && (
                      <div className="info-item mb-3">
                        <div className="d-flex justify-content-between">
                          <span className="font-weight-bold">Email:</span>
                          <span className="text-right text-muted">{invoice.publisher_id.email}</span>
                        </div>
                      </div>
                    )}
                    <div className="info-item mb-3">
                      <div className="d-flex justify-content-between">
                        <span className="font-weight-bold">Period:</span>
                        <span className="text-right">
                          {new Date(invoice.invoice_period?.start_date).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })} -{' '}
                          {new Date(invoice.invoice_period?.end_date).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })}
                        </span>
                      </div>
                    </div>
                    {/* {invoice.due_date && (
                      <div className="info-item mb-3">
                        <div className="d-flex justify-content-between">
                          <span className="font-weight-bold">Due Date:</span>
                          <span
                            className={`text-right ${new Date(invoice.due_date) < new Date() ? 'text-danger' : ''}`}
                          >
                            {new Date(invoice.due_date).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            })}
                          </span>
                        </div>
                      </div>
                    )} */}
                  </div>
                </Col>
                <Col md="6">
                  <div className="info-section">
                    <h6 className="text-muted mb-3">PAYMENT SUMMARY</h6>
                    <div className="payment-summary bg-light p-3 rounded">
                      <div className="d-flex justify-content-between mb-2">
                        <span>Subtotal:</span>
                        <span>{formatCurrency(calculatedTotals.subtotal)}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span>IGST(18%):</span>
                        <span>{formatCurrency(calculatedTotals.tax)}</span>
                      </div>
                      <hr className="my-2" />
                      <div className="d-flex justify-content-between mb-3">
                        <span className="font-weight-bold">Gross Total:</span>
                        <span className="font-weight-bold text-primary">
                          {formatCurrency(calculatedTotals.total)}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span>Less Tax deducted at source @ 2%</span>
                        <span>{formatCurrency(calculatedTotals.lesstax)}</span>
                      </div>
                      <hr className="my-2" />
                      <div className="d-flex justify-content-between mb-3">
                        <span className="fw-bold">Amount to be Paid:</span>
                        <span className="fw-bold text-primary">
                          {formatCurrency(calculatedTotals.amountToPaid)}
                        </span>
                      </div>
                      {invoice.totals?.paid_amount > 0 && (
                        <>
                          <div className="d-flex justify-content-between mb-2">
                            <span className="fw-bold text-success">Paid:</span>
                            <span className="fw-bold text-success">{formatCurrency(invoice.totals.paid_amount)}</span>
                          </div>
                          <div className="d-flex justify-content-between mb-3">
                            <span>Remaining:</span>
                            <span className="font-weight-bold">{formatCurrency(getRemainingAmount())}</span>
                          </div>
                          <div>
                            <div className="d-flex justify-content-between mb-1">
                              <small className="text-muted">Payment Progress</small>
                              <small className="text-muted">{Math.round(getPaymentProgress())}%</small>
                            </div>
                            <Progress 
                              value={getPaymentProgress()} 
                              color="success"
                              className="progress-sm"
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
        <Col lg="4">
          <Card className="shadow-sm text-center h-100">
            <CardBody className="d-flex flex-column justify-content-center">
              <div className="status-display">
                <div className="icon-big mb-3">
                  <i className={`nc-icon nc-paper text-${getStatusBadgeColor(invoice.status)}`} style={{ fontSize: '4rem' }} />
                </div>
                <h5 className="mb-3">Invoice Status</h5>
                <Badge 
                  color={getStatusBadgeColor(invoice.status)}
                  className="mb-3 text-capitalize px-4 py-2"
                  style={{ fontSize: '1.1rem' }}
                >
                  {invoice.status}
                </Badge>
                <div className="text-muted">
                  {invoice.status === 'paid' && '✓ Payment completed'}
                  {invoice.status === 'sent' && '⏳ Awaiting payment'}
                  {invoice.status === 'pending' && '🔄 Payment processing'}
                  {invoice.status === 'draft' && '📝 Not yet sent'}
                  {invoice.status === 'overdue' && '⚠️ Payment overdue'}
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {invoice.line_items?.length > 0 && (
        <Row className="mb-4">
          <Col>
            <Card className="shadow-sm">
              <CardHeader className="bg-white border-0">
                <h5 className="mb-0">
                  <i className="nc-icon nc-chart-bar-32 me-2" />
                  Revenue Summary
                </h5>
              </CardHeader>
              <CardBody className="p-0">
                <Table className="mb-0">
                  <thead className="thead-light">
                    <tr>
                      <th className="ps-3">Description</th>
                      <th className="text-right">Amount (INR)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const grouped = {
                        combo: 0,
                        monthly: 0,
                        yearly: 0,
                      };

                      const monthYear = new Date(invoice.invoice_period?.start_date).toLocaleDateString('en-GB', {
                        month: 'long',
                        year: 'numeric'
                      });

                      invoice.line_items.forEach(item => {
                        const desc = item.description?.toLowerCase() || '';

                        if (desc.includes('combo')) {
                          grouped.combo += item.net_amount;
                        } else if (desc.includes('monthly')) {
                          grouped.monthly += item.net_amount;
                        } else if (desc.includes('yearly')) {
                          grouped.yearly += item.net_amount;
                        }
                      });

                      const total = grouped.combo + grouped.monthly + grouped.yearly;

                      return (
                        <>
                          <tr>
                            <td className="ps-4">Revenue generated from Monthly Premium plan for {monthYear}</td>
                            <td className="text-right">{formatCurrency(grouped.monthly)}</td>
                          </tr>
                          <tr>
                            <td className="ps-4">Revenue generated from Yearly Premium plan for {monthYear}</td>
                            <td className="text-right">{formatCurrency(grouped.yearly)}</td>
                          </tr>
                          <tr>
                            <td className="ps-4">Revenue generated from Combo Premium plan for {monthYear}</td>
                            <td className="text-right">{formatCurrency(grouped.combo)}</td>
                          </tr>
                          <tr className="font-weight-bold">
                            <td className="ps-3"><strong>SubTotal</strong></td>
                            <td className="text-right"><strong>{formatCurrency(total)}</strong></td>
                          </tr>
                        </>
                      );
                    })()}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}


      {/* Line Items */}
      <Row className="mb-4">
        <Col>
          <Card className="shadow-sm">
            <CardHeader className="bg-white border-0">
              <h5 className="mb-0">
                <i className="nc-icon nc-bullet-list-67 me-2" />
                Line Items
              </h5>
            </CardHeader>
            <CardBody className="p-2">
              {invoice.line_items && invoice.line_items.length > 0 ? (
                <div className="table-responsive">
                  <Table className="align-items-center table-flush">
                    <thead className="thead-light">
                      <tr>
                        <th className="border-0">Description</th>
                        <th className="border-0 text-center">Qty</th>
                        <th className="border-0 text-right">Publisher Share</th>
                        <th className="border-0 text-right">IGST(18%)</th>
                        <th className="border-0 text-right">Net Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoice.line_items.map((item, idx) => (
                        <tr key={idx}>
                          <td className="border-0">
                            <div>
                              <span className="font-weight-bold">{item.description}</span>
                              {item.details && (
                                <div className="text-muted small mt-1">{item.details}</div>
                              )}
                            </div>
                          </td>
                          <td className="border-0 text-center">{item.quantity}</td>
                          <td className="border-0 text-right">{formatCurrency(item.net_amount)}</td>
                          <td className="border-0 text-right">{formatCurrency(item.net_amount * 0.18)}</td>
                          <td className="border-0 text-right">
                            <span className="font-weight-bold">{formatCurrency(item.net_amount + item.net_amount * 0.18)}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="thead-light">
                      <tr>
                        <th className="border-0 text-right" colSpan="4">Total:</th>
                        <th className="border-0 text-right">
                          <span className="font-weight-bold text-success">
                            {formatCurrency(calculatedTotals.total)}
                          </span>
                        </th>
                      </tr>
                    </tfoot>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-5">
                  <i className="nc-icon nc-bullet-list-67 text-muted" style={{ fontSize: '3rem' }} />
                  <h6 className="text-muted mt-3">No line items found</h6>
                  <p className="text-muted">This invoice doesn't contain any line items.</p>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Notes Section */}
      {invoice.notes && (
        <Row className="mb-4">
          <Col>
            <Card className="shadow-sm">
              <CardHeader className="bg-white border-0">
                <h5 className="mb-0">
                  <i className="nc-icon nc-single-copy-04 me-2" />
                  Notes
                </h5>
              </CardHeader>
              <CardBody>
                <p className="text-muted mb-0">{invoice.notes}</p>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* Payment Modal */}
      <Modal isOpen={paymentModal} toggle={togglePaymentModal} size="lg">
        <ModalHeader>
          Mark Invoice as Paid
        </ModalHeader>
        <ModalBody>
          <div className="mb-4 p-3 bg-light rounded">
            <Row>
              <Col md="6">
                <div className="text-center">
                  <h6 className="text-muted">Total Amount</h6>
                  <h4 className="text-primary">{formatCurrency(calculatedTotals.amountToPaid)}</h4>
                </div>
              </Col>
              <Col md="6">
                <div className="text-center">
                  <h6 className="text-muted">Remaining Balance</h6>
                  <h4 className="text-warning">{formatCurrency(getRemainingAmount())}</h4>
                </div>
              </Col>
            </Row>
          </div>
          
          <Form>
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label for="payment-amount">Payment Amount *</Label>
                  <Input
                    type="number"
                    id="payment-amount"
                    placeholder="Enter payment amount"
                    value={paymentDetails.amount || ''}
                    onChange={(e) => handlePaymentDetailChange('amount', e.target.value)}
                    step="0.01"
                    min="0"
                    max={getRemainingAmount()}
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label for="payment-date">Payment Date *</Label>
                  <Input
                    type="date"
                    id="payment-date"
                    value={paymentDetails.payment_date}
                    onChange={(e) => handlePaymentDetailChange('payment_date', e.target.value)}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label for="payment-method">Payment Method</Label>
                  <Input
                    type="select"
                    id="payment-method"
                    value={paymentDetails.payment_method}
                    onChange={(e) => handlePaymentDetailChange('payment_method', e.target.value)}
                  >
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="credit_card">Credit Card</option>
                    <option value="debit_card">Debit Card</option>
                    <option value="cash">Cash</option>
                    <option value="check">Check</option>
                    <option value="online_payment">Online Payment</option>
                    <option value="other">Other</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label for="reference-number">Reference Number</Label>
                  <Input
                    type="text"
                    id="reference-number"
                    placeholder="Transaction/Reference ID"
                    value={paymentDetails.reference_number}
                    onChange={(e) => handlePaymentDetailChange('reference_number', e.target.value)}
                  />
                </FormGroup>
              </Col>
            </Row>
            <FormGroup>
              <Label for="payment-notes">Notes</Label>
              <Input
                type="textarea"
                id="payment-notes"
                placeholder="Additional notes about this payment..."
                rows="3"
                value={paymentDetails.notes}
                onChange={(e) => handlePaymentDetailChange('notes', e.target.value)}
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={togglePaymentModal} >
            <i className="nc-icon nc-simple-remove" />
            Cancel
          </Button>
          <Button 
            color="success" 
            onClick={handleMarkAsPaid}
            disabled={markingPaid || paymentDetails.amount <= 0}
            className="mx-2"
          >
            {markingPaid ? (
              <>
                <Spinner size="sm" className="me-2" />
                Processing...
              </>
            ) : (
              <>
                <i className="nc-icon nc-check-2 me-1" />
                Mark as Paid
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={modalOpen} toggle={toggleModal} size="lg">
        <ModalHeader
          style={{
            borderBottom: 'none',
            padding: '1.5rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <h5 className="m-0">Invoice Preview</h5>
        </ModalHeader>

        <ModalBody style={{ maxHeight: '68vh', overflowY: 'auto' }}>
          <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
        </ModalBody>

        <div className="d-flex justify-content-end gap-2 border-top px-4 py-3">
          <Button color="danger" onClick={toggleModal}>
            <i className="nc-icon nc-simple-remove" /> Close
          </Button>
          <Button color="success" onClick={() => {
            handleDownloadPdf();
            toggleModal();
          }}>
            <i className="nc-icon nc-cloud-download-93 me-1" />
            {downloading ? 'Downloading...' : ' Download PDF'}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default InvoiceDetail;